import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="drawer-frame-trigger"
//
// Opens a shared DrawerComponent and lets Turbo load the clicked row's content
// into that drawer's turbo-frame. One drawer serves a whole table, so an N-row
// table costs one fixed-position drawer rather than N.
//
// Dispatches `drawer:open` WITHOUT calling preventDefault, so Turbo can still
// process the link's `data-turbo-frame` attribute and load the content.
//
// drawer_controller#open is the wrong fit here — it preventDefaults to stop
// button clicks from submitting forms / following hrefs, which is fine for
// buttons but cancels the Turbo Frame fetch on link triggers.
export default class extends Controller {
  static values = {
    // Turbo-frame id inside the drawer that the content loads into.
    frame: String,
    // DrawerComponent identifier to open.
    instance: String,
    // Wrapper classes for the loading state. The default suits a
    // DrawerComponent(content_frame:) drawer, whose frame owns its own scrolling.
    // A drawer whose frame sits in the component's default content area already
    // has padding and scrolling, so it passes "".
    loadingClass: { type: String, default: "drawer-scroll px-4 sm:px-6 py-6" },
    // "v2" shows the design system's loader (.v2-loader) with loadingLabel. V1 pages keep the default:
    // their stylesheet has no .v2-loader.
    loaderStyle: { type: String, default: "v1" },
    loadingLabel: { type: String, default: "Loading…" },
    // Optional row label written into the drawer header before the fetch, so the
    // header names the clicked row rather than waiting on the response.
    title: String,
  }

  open() {
    // A blank instance would match every drawer on the page (drawer_instance#open
    // compares loosely), so refuse rather than open all of them.
    if (!this.instanceValue) {
      console.warn("drawer-frame-trigger: missing instance value, refusing to open")
      return
    }

    this.#setHeaderTitle()

    // Swap in a loading state so the user doesn't stare at the previous row's
    // content while Turbo fetches this one.
    const frame = document.getElementById(this.frameValue)
    if (frame && this.loaderStyleValue === "v2") {
      frame.replaceChildren(this.#v2Loading())
    } else if (frame) {
      frame.innerHTML = `
        <div class="${this.loadingClassValue}">
          <div class="flex items-center text-sm text-gray-500 italic">
            <svg class="animate-spin h-4 w-4 mr-2 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
            </svg>
            Loading…
          </div>
        </div>
      `
    }

    this.dispatch("open", {
      prefix: "drawer",
      target: window,
      detail: { instance: this.instanceValue },
    })
  }

  // V2::LoaderComponent's centered small loader, built as nodes so the label is
  // text rather than markup.
  #v2Loading() {
    const wrapper = document.createElement("div")
    wrapper.className = this.loadingClassValue
    const loader = document.createElement("div")
    loader.className = "v2-loader v2-loader-sm v2-loader-center"
    loader.textContent = this.loadingLabelValue
    wrapper.append(loader)
    return wrapper
  }

  // The header slot is conventionally the frame id plus "-title"; drawers that
  // don't want a per-row title simply omit the element.
  #setHeaderTitle() {
    if (!this.hasTitleValue) return

    const slot = document.getElementById(`${this.frameValue}-title`)
    if (slot) slot.textContent = this.titleValue
  }
}
